import { Stack, Typography, FormLabel, SvgIcon, Box, IconButton } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { LegalDocument } from '@pxtra-client-lib-legacy/gen';
import GoogleLogo from '../../lib/logos/Google.svg?react';
import MicrosoftLogo from '../../lib/logos/Microsoft.svg?react';
import MailIcon from '../../lib/icons/svg/mail.svg?react';
import LockIcon from '../../lib/icons/svg/lock.svg?react';
import EyeOpenIcon from '../../lib/icons/svg/eye_open.svg?react';
import EyeClosedIcon from '../../lib/icons/svg/eye_closed.svg?react';
import { Input, TwoFactorCodeInput, AgreementDialog } from '../../lib/components';
import { Button } from '../../lib/components/atomic/button';
import { useStore } from '../../hooks/useStore';
import { useSnackbar } from 'notistack';
import { Header } from './partials/Header';
import { LoginLogo } from './partials/LoginLogo';

interface From {
  pathname: string;
}

interface LocationState {
  from: From;
}

const MAX_WIDTH = '400px';
const MAIL_ICON = <SvgIcon component={MailIcon} />;

const Login = () => {
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showAgreementDialog, setShowAgreementDialog] = useState(false);
  const [currentDialogIndex, setCurrentDialogIndex] = useState(0);
  const [requestCodeDisabled, setRequestCodeDisabled] = useState(false);
  const [codeValid, setCodeValid] = useState(false);
  const [twoFactorNextStep, setTwoFactorNextStep] = useState(false);
  const [unacceptedAgreements, setUnacceptedAgreements] = useState<LegalDocument[]>([]);
  const { t, i18n } = useTranslation();
  const snackbar = useSnackbar();

  function navigateToRequestedPage() {
    if (hasFromState(location.state)) {
      const fromPath = location.state.from.pathname;
      const authPaths = ['/login', '/forgot-password', '/reset-password'];
      const isAuthPath = authPaths.some((path) => fromPath.startsWith(path));

      if (!isAuthPath) {
        navigate(fromPath);
      } else {
        navigate('/dashboard');
      }
    } else {
      navigate('/dashboard');
    }
  }

  async function preventEmployeeLogin() {
    await companyStore.fetchManagedCompanies();

    if ((companyStore.managedCompanies?.length ?? 0) > 0) {
      navigateToRequestedPage();
    } else {
      await authenticationStore.logout();
      snackbar.enqueueSnackbar({
        variant: 'detailedError',
        message: t('account:ERROR_MESSAGE'),
        details: t('account:ERROR_LOGIN_EMPLOYEE')
      });
    }
  }

  const postLogin = async () => {
    const fetchedAgreements = await authenticationStore.fetchAgreements(false);
    setUnacceptedAgreements(fetchedAgreements);
    if (fetchedAgreements.length > 0) {
      setShowAgreementDialog(true);
    } else {
      await preventEmployeeLogin();
    }
  };

  const formikFirstStep = useFormik({
    initialValues: {
      username: '',
      password: ''
    },
    validationSchema: Yup.object().shape({
      username: Yup.string().email(t('form:INVALID_EMAIL')).required(t('account:ERROR_EMAIL')),
      password: Yup.string().required(t('account:ERROR_PASSWORD'))
    }),
    onSubmit: async (values) => {
      const { username, password } = values;
      setFirstStepError('');
      try {
        const twoFactorNextStepResponse = await authenticationStore.loginFirstStep(
          username,
          password
        );
        setTwoFactorNextStep(twoFactorNextStepResponse);
        if (twoFactorNextStepResponse) {
          setShowLoginForm(false);
          return;
        }
      } catch (e) {
        if ([429].includes((e as { status: number }).status)) {
          snackbar.enqueueSnackbar({
            variant: 'error',
            message: t('account:ERROR_RATE_LIMIT')
          });
        } else {
          snackbar.enqueueSnackbar({
            variant: 'detailedError',
            message: t('account:ERROR_MESSAGE'),
            details: t('account:ERROR_LOGIN_DATA')
          });
        }

        return;
      }
      await postLogin();
    }
  });

  const submitCode = async (code: string) => {
    setSecondStepError(false);
    try {
      await authenticationStore.loginSecondStep(code);
      setCodeValid(true);
    } catch {
      snackbar.enqueueSnackbar({
        variant: 'detailedError',
        message: t('account:ERROR_MESSAGE'),
        details: t('account:ERROR_LOGIN_CODE')
      });
      return;
    }

    await postLogin();
  };

  const requestCode = async () => {
    let timeout = 0;

    try {
      setRequestCodeDisabled(true);
      timeout = Number(setTimeout(() => setRequestCodeDisabled(false), 60000));
      await authenticationStore.loginFirstStep(
        formikFirstStep.values.username,
        formikFirstStep.values.password
      );
      snackbar.enqueueSnackbar({
        variant: 'detailedSuccess',
        message: t('auth:LOGIN_2FA_REQUEST_CODE_SUCCESS_HEADLINE'),
        details: t('auth:LOGIN_2FA_REQUEST_CODE_SUCCESS_SUBTEXT')
      });
      setCodeValid(false);
    } catch {
      clearTimeout(timeout);
      setRequestCodeDisabled(false);
    }
  };

  const hasUsernameError = formikFirstStep.touched.username && !!formikFirstStep.errors.username;
  const hasPasswordError = formikFirstStep.touched.password && !!formikFirstStep.errors.password;

  const [firstStepError, setFirstStepError] = useState('');
  const [secondStepError, setSecondStepError] = useState(false);

  const { authenticationStore, companyStore } = useStore();
  const navigate = useNavigate();
  const location = useLocation();

  const [searchParams] = useSearchParams();
  const hasFromState = (state: unknown): state is LocationState => {
    return typeof state === 'object' && state !== null && 'from' in state;
  };

  const handleCloseAndShowNextDialog = () => {
    setCurrentDialogIndex(currentDialogIndex + 1);
    setShowAgreementDialog(false);
  };

  const handleBackClick = () => {
    if (!showLoginForm && twoFactorNextStep) {
      formikFirstStep.values.password = '';
      setTwoFactorNextStep(false);
      setShowLoginForm(true);
    } else {
      window.requestAnimationFrame(() => {
        navigate('login', { replace: true });
      });
    }
  };

  useEffect(() => {
    if (authenticationStore.isLoggedIn) {
      navigate('/');
    }
  }, []);

  useEffect(() => {
    if (searchParams.get('form')) {
      setShowLoginForm(true);
    }
  }, [searchParams]);

  useEffect(() => {
    if (authenticationStore.isLoggedIn) {
      if (!showAgreementDialog && currentDialogIndex < unacceptedAgreements.length) {
        setShowAgreementDialog(true);
      } else if (!showAgreementDialog && currentDialogIndex === unacceptedAgreements.length) {
        setUnacceptedAgreements([]);
        preventEmployeeLogin();
      }
    }
  }, [showAgreementDialog]);

  useEffect(() => {
    const browserLanguage = navigator.language || navigator.languages?.[0] || 'en';
    const targetLanguage = browserLanguage.toLowerCase().startsWith('de') ? 'de' : 'en';
    i18n.changeLanguage(targetLanguage);
  }, []);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '400px'
      }}
    >
      <Header showBackButton={showLoginForm || twoFactorNextStep} onBackClick={handleBackClick} />
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: '24px',
          padding: '0 32px 64px 32px',
          margin: '20px auto',
          maxWidth: '500px',
          width: '100%'
        }}
      >
        <LoginLogo />
        {showLoginForm && !twoFactorNextStep && (
          <Stack
            component="form"
            spacing={2}
            sx={{
              maxWidth: MAX_WIDTH,
              margin: '0 auto',
              '& .MuiInput-root': {
                borderRadius: '16px'
              }
            }}
            onSubmit={formikFirstStep.handleSubmit}
          >
            <Box sx={{ width: '100%', alignSelf: 'flex-start', paddingBottom: '16px' }}>
              <Typography component="h1" textAlign="left" color="primary.main" lineHeight="24px">
                {t('auth:LOGIN_INFO')}
              </Typography>
            </Box>
            <Input
              type="email"
              name="username"
              id="username"
              placeholder={t('auth:email')}
              value={formikFirstStep.values.username}
              onChange={formikFirstStep.handleChange}
              onBlur={formikFirstStep.handleBlur}
              startAdornment={MAIL_ICON}
              error={!!firstStepError || hasUsernameError}
              sx={{ gap: '0.75rem' }}
            />
            {hasUsernameError && (
              <FormLabel sx={{ marginTop: '0.5rem!important' }} error>
                {formikFirstStep.errors.username}
              </FormLabel>
            )}
            <Input
              type={showPassword ? 'text' : 'password'}
              name="password"
              id="password"
              placeholder={t('auth:password')}
              value={formikFirstStep.values.password}
              onChange={formikFirstStep.handleChange}
              onBlur={formikFirstStep.handleBlur}
              startAdornment={<SvgIcon component={LockIcon} />}
              endAdornment={
                <IconButton
                  aria-label={t('auth:showPasswordHint')}
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  <SvgIcon component={!showPassword ? EyeOpenIcon : EyeClosedIcon} />
                </IconButton>
              }
              error={!!firstStepError || hasPasswordError}
            />
            {hasPasswordError && (
              <FormLabel sx={{ marginTop: '0.5rem!important' }} error>
                {formikFirstStep.errors.password}
              </FormLabel>
            )}
            <Button type="submit" variant="primary" className="w-full">
              {t('auth:login')}
            </Button>
            <NavLink to="/forgot-password" style={{ textDecoration: 'none' }}>
              <Typography
                variant="body1"
                color="primary"
                sx={{
                  textAlign: 'center',
                  textDecoration: 'none',
                  marginTop: '2rem !important'
                }}
              >
                {t('account:FORGOT_PASSWORD_QUESTION')}
              </Typography>
            </NavLink>
          </Stack>
        )}
        {!showLoginForm && twoFactorNextStep && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              maxWidth: MAX_WIDTH,
              margin: '0 auto',
              '& .MuiInput-root': {
                borderRadius: '16px'
              }
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'start',
                alignItems: 'start',
                maxWidth: MAX_WIDTH,
                margin: '0 auto',
                '& .MuiInput-root': {
                  borderRadius: '16px'
                }
              }}
            >
              <Typography
                variant="body1"
                component="p"
                className="text-primary-main leading-7 font-bold text-lg mb-2"
              >
                {t('account:2FA_AUTH')}
              </Typography>
              <Typography
                variant="body1"
                component="p"
                className="text-primary-main leading-6 mb-8"
              >
                {t('auth:LOGIN_2FA_INFO', { email: formikFirstStep.values.username })}
              </Typography>
            </Box>

            <TwoFactorCodeInput
              submitCode={submitCode}
              codeValid={codeValid}
              error={secondStepError}
              setError={setSecondStepError}
            />
            <Button
              onClick={requestCode}
              disabled={requestCodeDisabled}
              variant="secondary"
              className="border-none font-normal hover:border-none disabled:border-none disabled:text-primary-light mt-1"
            >
              {t('auth:LOGIN_2FA_REQUEST_CODE')}
            </Button>
          </Box>
        )}

        {!showLoginForm && !twoFactorNextStep && (
          <Box
            sx={{
              maxWidth: MAX_WIDTH,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              margin: '0 auto',
              gridRowGap: '0.5rem',
              width: '400px'
            }}
          >
            <Box sx={{ width: '100%', alignSelf: 'flex-start', paddingBottom: '16px' }}>
              <Typography component="h1" textAlign="left" color="primary.main" lineHeight="24px">
                {t('auth:LOGIN_INFO')}
              </Typography>
            </Box>

            <Button variant="primary" onClick={() => setShowLoginForm(true)} className="w-full">
              {t('auth:loginWithEmail')}
            </Button>

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                paddingY: '16px'
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  alignSelf: 'flex-start',
                  borderTop: '1px solid',
                  borderColor: 'grey.300',
                  margin: '10px 10px 0 0'
                }}
              ></Box>
              <Typography color="grey.600">{t('common:OR')}</Typography>{' '}
              <Box
                sx={{
                  width: '100%',
                  alignSelf: 'flex-start',
                  borderTop: '1px solid',
                  borderColor: 'grey.300',
                  margin: '10px 0 0 10px'
                }}
              ></Box>
            </Box>

            <Button
              variant="secondary"
              onClick={async () => (window.location.href = authenticationStore.getGoogleLoginUrl())}
              className="text-primary-main font-medium bg-white w-full"
            >
              <SvgIcon component={GoogleLogo} />
              {t('auth:loginWithGoogle')}
            </Button>

            <Button
              variant="secondary"
              onClick={async () =>
                (window.location.href = authenticationStore.getMicrosoftLoginUrl())
              }
              className="text-primary-main font-medium bg-white w-full"
            >
              <SvgIcon component={MicrosoftLogo} />
              {t('auth:loginWithMicrosoft')}
            </Button>
          </Box>
        )}
        <div>
          {unacceptedAgreements.map(
            (agreement, index) =>
              index === currentDialogIndex && (
                <AgreementDialog
                  key={agreement.id}
                  open={showAgreementDialog}
                  title={t('agreements:heading1') + agreement.title + t('agreements:heading2')}
                  contentText={t('agreements:scrollHint', { title: agreement.title })}
                  contentHtml={agreement.content}
                  onConfirm={() => {
                    authenticationStore.submitAgreement(agreement.id);
                    handleCloseAndShowNextDialog();
                  }}
                  confirmButtonText={t('common:CONFIRM')}
                  checkboxText={t('agreements:agreement', { title: agreement.title })}
                />
              )
          )}
        </div>
      </Box>
    </Box>
  );
};

export default observer(Login);
